import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, filter, take } from 'rxjs';

export interface ScopeInfoDto {
  scopeType: string;       // "Fund" or "Investor"
  scopeRequired: boolean;  // IMRSC_IsScopeRestricted
  allowAll: boolean;       // IMRSC_AllowAll
}

export interface EffectivePermissionsDto {
  navPermissions: string[];
  viewPermissions: string[];
  actionPermissions: string[];
  permissionCodes: string[];
  scopeConfigurations?: ScopeInfoDto[];  // NEW
}
interface NavPermissionItem {
  route?: string;
  permissionKey?: string;
  permissionKeys?: string[];  // add this line
}
@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private navPermissions = new Set<string>();
  private viewPermissions = new Set<string>();
  private actionPermissions = new Set<string>();

  // Keyed by normalized (lowercased) ScopeType, e.g. "fund", "investor".
  // A user's role(s) may have zero, one, or more scope config rows —
  // absence of a key means "no scope restriction configured" for that type.
  private scopeConfigurations = new Map<string, ScopeInfoDto>();

  private _isLoaded = false;
  private loaded$ = new BehaviorSubject<boolean>(false);

  constructor(  private router: Router,) {
    this.hydrateFromSession();
  }

  get isLoaded(): boolean {
    return this._isLoaded;
  }

  loaded(): Observable<boolean> {
    return this.loaded$.pipe(filter(v => v === true), take(1));
  }
getFirstAccessibleNavRoute(items: NavPermissionItem[]): string | null {
  for (const item of items) {
    if (!item.route) {
      continue;
    }
 if (item.permissionKey && this.canView(item.permissionKey)) {
      return item.route;
    }
    // Group item: check each key in priority order, resolve real route via map
    if (item.permissionKeys?.length) {
      const landingRoute = this.getAdminOpsLandingRoute(item.permissionKeys);
      if (landingRoute) {
        return landingRoute;
      }
    }
  }

  return null;
}
getFirstAccessibleRoute(items: NavPermissionItem[]): string | null {
  return this.getFirstAccessibleNavRoute(items); // same logic, reusable name for group-level lookups
}
canNavAny(featureCodes: string[]): boolean {
  return featureCodes.some(code => this.navPermissions.has(`NAV.${code}`));
}

canViewAny(featureCodes: string[]): boolean {
  return featureCodes.some(code => this.viewPermissions.has(`VIEW.${code}`));
}
  /** Called once by login.component after GetTreeNode returns. */
  setPermissions(dto: EffectivePermissionsDto): void {
    this.navPermissions = new Set(dto.navPermissions ?? []);
    this.viewPermissions = new Set(dto.viewPermissions ?? []);
    this.actionPermissions = new Set(dto.actionPermissions ?? []);

    this.scopeConfigurations = this.buildScopeMap(dto.scopeConfigurations ?? []);

    this._isLoaded = true;
    this.loaded$.next(true);

    sessionStorage.setItem('NavPermissions', JSON.stringify(dto.navPermissions ?? []));
    sessionStorage.setItem('ViewPermissions', JSON.stringify(dto.viewPermissions ?? []));
    sessionStorage.setItem('ActionPermissions', JSON.stringify(dto.actionPermissions ?? []));
    sessionStorage.setItem('ScopeConfigurations', JSON.stringify(dto.scopeConfigurations ?? []));
  }

  private buildScopeMap(scopes: ScopeInfoDto[] | null | undefined): Map<string, ScopeInfoDto> {
    const map = new Map<string, ScopeInfoDto>();

    if (!scopes || !Array.isArray(scopes)) {
      return map;
    }

    for (const scope of scopes) {
      if (!scope?.scopeType) {
        // Skip malformed / null entries rather than throwing.
        continue;
      }
      const key = scope.scopeType.toLowerCase();
      map.set(key, {
        scopeType: scope.scopeType,
        scopeRequired: !!scope.scopeRequired,
        allowAll: !!scope.allowAll
      });
    }

    return map;
  }

  private hydrateFromSession(): void {
    const nav = this.readSessionArray('NavPermissions');
    const view = this.readSessionArray('ViewPermissions');
    const action = this.readSessionArray('ActionPermissions');
    const scope = this.readSessionScopeArray('ScopeConfigurations');

    if (nav.length || view.length || action.length || scope.length) {
      this.navPermissions = new Set(nav);
      this.viewPermissions = new Set(view);
      this.actionPermissions = new Set(action);
      this.scopeConfigurations = this.buildScopeMap(scope);
      this._isLoaded = true;
      this.loaded$.next(true);
    }
  }

  private readSessionArray(key: string): string[] {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }

  private readSessionScopeArray(key: string): ScopeInfoDto[] {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? (JSON.parse(raw) as ScopeInfoDto[]) : [];
    } catch {
      return [];
    }
  }

  canNav(featureCode: string): boolean {
    return this.navPermissions.has(`NAV.${featureCode}`);
  }

  canView(featureCode: string): boolean {
    return this.viewPermissions.has(`VIEW.${featureCode}`);
  }

  canPerformAction(featureCode: string, actionCode: string): boolean {
    return this.actionPermissions.has(`ACTION.${featureCode}.${actionCode}`);
  }

  // ============================================================
  // SCOPE
  // ============================================================

  /** Raw scope config for a type ("Fund" / "Investor"), or undefined if none configured. */
  getScope(scopeType: string): ScopeInfoDto | undefined {
    if (!scopeType) return undefined;
    return this.scopeConfigurations.get(scopeType.toLowerCase());
  }

  /** True only if a scope row exists for this type AND it is marked restricted. */
  isScopeRequired(scopeType: string): boolean {
    return this.getScope(scopeType)?.scopeRequired ?? false;
  }

  /** True only if a scope row exists for this type AND allowAll is set. Defaults to false when unconfigured. */
  isScopeAllowAll(scopeType: string): boolean {
    return this.getScope(scopeType)?.allowAll ?? false;
  }

  /** True if any scope configuration exists at all for this type. */
  hasScopeType(scopeType: string): boolean {
    if (!scopeType) return false;
    return this.scopeConfigurations.has(scopeType.toLowerCase());
  }

  /** All scope types currently configured for this user (e.g. ["Fund", "Investor"]). */
  getConfiguredScopeTypes(): string[] {
    return Array.from(this.scopeConfigurations.values()).map(s => s.scopeType);
  }

  clear(): void {
    this.navPermissions.clear();
    this.viewPermissions.clear();
    this.actionPermissions.clear();
    this.scopeConfigurations.clear();
    this._isLoaded = false;
    this.loaded$.next(false);
  }
private readonly ADMIN_OPS_ROUTE_MAP: Record<string, string> = {
  DocTemplate: '/Doc-management/Documentvault',
    DOCVariables: '/Doc-management/managevariables',
      DocUpload: '/Doc-management/Standarddocument',
  DocBulkUpload: '/Doc-management/BulkUploadview',

  Profile: '/Admin-ops/improfile',
  Branding: '/Admin-ops/BrandingLogo',
  Staff: '/Admin-ops/Adminops',
  CapitalCallPayment: '/Admin-ops/AllocationPriority',
  UserManagement: '/Admin-ops/UserManagement',
  Role: '/Admin-ops/RoleBasedAccess',
  UserAccessLog: '/Admin-ops/LoginHistory',
  AuthenticationPolicy: '/Admin-ops/LoginSetting',
  Storage: '/Admin-ops/StorageConfiguration',
  Integration: '/Admin-ops/thirdpartyappsetting',
  EmailConfiguration: '/Admin-ops/SMTPSetup',
  EventCatalog: '/Admin-ops/Events',
  NotificationLog: '/Admin-ops/EmailLog',
  NotificationChannel: '/Admin-ops/NotificationChannel',
  Configguration: '/Admin-ops/SMTPSetup',
  AuditLog: '/Admin-ops/AuditLog',
  ReferenceData: '/Admin-ops/managecodemaster',
    Subscription: '/Admin-ops/SubscriptionManagement',

  // Document Management
};
checkIdentityAccessRoute(): void {
  const routeMap: Record<string, string> = {
    UserManagement: '/Admin-ops/UserManagement',
    Role: '/Admin-ops/RoleBasedAccess',
    UserAccessLog: '/Admin-ops/LoginHistory',
    AuthenticationPolicy: '/Admin-ops/LoginSetting'
  };

  const currentKey = Object.keys(routeMap).find(
    key => routeMap[key] === this.router.url
  );

  if (currentKey && !this.canView(currentKey)) {
    const fallback = Object.keys(routeMap).find(key =>
      this.canView(key)
    );

    if (fallback) {
      this.router.navigate([routeMap[fallback]]);
    }
  }
}
checkGovernanceRoute(): void {
  const routeMap: Record<string, string> = {
    AuditLog: '/Admin-ops/AuditLog',
    ReferenceData: '/Admin-ops/managecodemaster'
  };

  const currentKey = Object.keys(routeMap).find(
    key => routeMap[key] === this.router.url
  );

  if (currentKey && !this.canView(currentKey)) {
    const fallback = Object.keys(routeMap).find(key =>
      this.canView(key)
    );

    if (fallback) {
      this.router.navigate([routeMap[fallback]]);
    }
  }
}
checkSubscriptionRoute(): void {
  const routeMap: Record<string, string> = {
    Subscription: '/Admin-ops/SubscriptionManagement'
  };

  const currentKey = Object.keys(routeMap).find(
    key => routeMap[key] === this.router.url
  );

  if (currentKey && !this.canView(currentKey)) {
    const fallback = Object.keys(routeMap).find(key =>
      this.canView(key)
    );

    if (fallback) {
      this.router.navigate([routeMap[fallback]]);
    }
  }
}
checkOrganizationRoute(): void {
  const routeMap: Record<string, string> = {
    Profile: '/Admin-ops/improfile',
    Branding: '/Admin-ops/BrandingLogo',
    Staff: '/Admin-ops/Adminops',
    CapitalCallPayment: '/Admin-ops/AllocationPriority'
  };

  const currentKey = Object.keys(routeMap).find(
    key => routeMap[key] === this.router.url
  );

  if (currentKey && !this.canView(currentKey)) {
    const fallback = Object.keys(routeMap).find(key =>
      this.canView(key)
    );

    if (fallback) {
      this.router.navigate([routeMap[fallback]]);
    }
  }
}
checkCommunicationRoute(): void {
  const routeMap: Record<string, string> = {
    EmailConfiguration: '/Admin-ops/SMTPSetup',
    EventCatalog: '/Admin-ops/Events',
    NotificationLog: '/Admin-ops/EmailLog',
    Configguration: '/Admin-ops/Configuration',
    NotificationChannel: '/Admin-ops/NotificationChannel'
  };

  const currentKey = Object.keys(routeMap).find(
    key => routeMap[key] === this.router.url
  );

  if (currentKey && !this.canView(currentKey)) {
    const fallback = Object.keys(routeMap).find(key =>
      this.canView(key)
    );

    if (fallback) {
      this.router.navigate([routeMap[fallback]]);
    }
  }
}
checkStorageIntegrationRoute(): void {
  const routeMap: Record<string, string> = {
    Storage: '/Admin-ops/StorageConfiguration',
    Integration: '/Admin-ops/thirdpartyappsetting'
  };

  const currentKey = Object.keys(routeMap).find(
    key => routeMap[key] === this.router.url
  );

  if (currentKey && !this.canView(currentKey)) {
    const fallback = Object.keys(routeMap).find(key =>
      this.canView(key)
    );

    if (fallback) {
      this.router.navigate([routeMap[fallback]]);
    }
  }
}
/** Returns the first route in priority order the user is allowed to nav to, or null. */
getAdminOpsLandingRoute(candidateKeys: string[]): string | null {
  const key = candidateKeys.find(k => this.canNav(k));
  return key ? this.ADMIN_OPS_ROUTE_MAP[key] : null;
}
}